import React, { MutableRefObject, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import log from 'shared/utils/log';
import { SbRrwebEvents } from 'sb-lib/types';
import { setWasPlayDeniedNoInteraction } from 'shared/store/dashboard-slice';
import type { AppState } from 'shared/store/reducers';
import { isPlayNoInteractionError } from 'shared/utils/helpers';
import {
  fixFlippedMaximizedContentW20,
  fixFlippedWidgetVideo,
  getInjectorVideoElements,
} from 'shared/utils/widget-injector';
import { IncomingStreamAudio } from 'shared/components/IncomingStreamAudio';
import {
  subscribeToRrweb,
  subscribeToWidgetRrweb,
  unsubscribeToRrweb,
  unsubscribeToWidgetRrweb,
} from 'shared/realtime';
import './style.less';
import { useAppSelector } from 'shared/store';
import {
  selectIncomingCameraVideoStream,
  selectOutgoingCameraStream,
  selectOutgoingDisplayStream,
} from 'shared/store/realtime-slice';

// Detect widget video elements in rrweb replayer iframe
// and inject local/remote streams
export default function WidgetVideoInjector({
  rrwebIframeWrapper,
  isWidgetStream,
}: {
  rrwebIframeWrapper: MutableRefObject<HTMLDivElement | null>;
  isWidgetStream: boolean;
}) {
  let sub = subscribeToRrweb;
  let unsub = unsubscribeToRrweb;
  if (isWidgetStream) {
    sub = subscribeToWidgetRrweb;
    unsub = unsubscribeToWidgetRrweb;
  }
  useWidgetVideoInjector(rrwebIframeWrapper, sub, unsub);
  const { wasPlayDeniedNoInteraction } = useSelector((s: AppState) => s.dashboard);
  const dispatch = useDispatch();
  return (
    <>
      <IncomingStreamAudio />
      <Modal
        visible={wasPlayDeniedNoInteraction}
        className="WidgetVideoInjector__modal"
        onOk={() => dispatch(setWasPlayDeniedNoInteraction(false))}
        onCancel={() => dispatch(setWasPlayDeniedNoInteraction(false))}
        okText="Ok!"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        You won't be able to see the visitor until you interact with the page. Click OK to
        do so now.
      </Modal>
    </>
  );
}

const useWidgetVideoInjector = (
  rrwebIframeWrapper: MutableRefObject<HTMLDivElement | null>,
  rrwebSubscribe: (f: (event: SbRrwebEvents) => void) => void,
  rrwebUnsubscribe: (f: (event: SbRrwebEvents) => void) => void,
) => {
  const [mainVideo, setMainVideo] = useState<null | HTMLVideoElement>(null);
  const [subVideo, setSubVideo] = useState<null | HTMLVideoElement>(null);
  const [demoVideo, setDemoVideo] = useState<null | HTMLVideoElement>(null);
  const { wasPlayDeniedNoInteraction, isOutgoingVideoEnabled } = useAppSelector(
    (s: AppState) => s.dashboard,
  );
  const { isReceivingAudio, isReceivingVideo } = useAppSelector(s => s.realtime);
  const camStreamOut = useAppSelector(selectOutgoingCameraStream);
  const camVideoStreamIn = useAppSelector(selectIncomingCameraVideoStream);
  const displayStreamOut = useAppSelector(selectOutgoingDisplayStream);
  const dispatch = useDispatch();

  // extract video elements from rrweb iframe
  useEffect(() => {
    const getCurrentRrwebPlayerIframe = () => {
      if (rrwebIframeWrapper.current) {
        const iframes = rrwebIframeWrapper.current.getElementsByTagName('iframe');
        if (iframes.length) {
          return iframes[0];
        }
      }
      return null;
    };
    const updateVideoRefs = () => {
      const iframe = getCurrentRrwebPlayerIframe();
      const videos = getInjectorVideoElements(iframe);
      if (videos) {
        setMainVideo(videos.main);
        setSubVideo(videos.sub);
        setDemoVideo(videos.demo);
      }
    };
    rrwebSubscribe(updateVideoRefs);
    return () => rrwebUnsubscribe(updateVideoRefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // manage camera media elements (play, pause, inject self-stream)
  useEffect(() => {
    if (!mainVideo) {
      return;
    }

    // Only flip videos if it's the widget rendering, and we're receiving video.
    // see: /widget/src/app/components/Video/index.tsx for data-renderer attribute.
    const isDisplaydemoVideos = mainVideo.getAttribute('data-renderer') === 'displaydemo';
    const flipVideos = !isDisplaydemoVideos && subVideo && isReceivingVideo;

    const mainVideoSrc = flipVideos ? camVideoStreamIn : camStreamOut;
    const subVideoSrc = flipVideos ? camStreamOut : camVideoStreamIn;

    // Update video src's and play videos. Handle media play interaction errors too.
    // Videos are always muted because we have <audio/> elements to play audio instead.
    // Handle no-interaction errors if we try to play video before page interaction.
    const updateVideo = (
      name: string,
      video: HTMLVideoElement,
      srcObject: MediaStream | null,
    ) => {
      video.muted = true;
      video.srcObject = srcObject;

      log.debug(
        `Updated <video /> with stream: ${srcObject?.getTracks().map(t => {
          return JSON.stringify({
            id: t.id,
            kind: t.kind,
            enabled: t.enabled,
            muted: t.muted,
          });
        })}`,
      );

      video.play().catch(error => {
        if (isPlayNoInteractionError(error)) {
          log.log(
            `Failed to play ${name} due to lack of interaction, will retry when interacted with`,
            error,
          );
          // tell redux about this (will elicit interaction from admin)
          dispatch(setWasPlayDeniedNoInteraction(true));
          // set src null so that we re-run updateVideo when interaction is done
          video.srcObject = null;
        } else {
          log.log(`There was an error trying to play ${name}`, error);
        }
      });
    };

    if (mainVideo.srcObject !== mainVideoSrc) {
      log.debug(`Updating main video`);
      updateVideo('large video', mainVideo, mainVideoSrc);
    }
    if (subVideo && subVideo.srcObject !== subVideoSrc) {
      log.debug(`Updating sub video`);
      updateVideo('small self-video', subVideo, subVideoSrc);
    }

    if (!isDisplaydemoVideos) {
      fixFlippedWidgetVideo(!!flipVideos, isOutgoingVideoEnabled, mainVideo, subVideo);
      fixFlippedMaximizedContentW20(!!flipVideos, mainVideo, subVideo);
    }
  }, [
    camStreamOut,
    camVideoStreamIn,
    isReceivingVideo,
    isReceivingAudio,
    isOutgoingVideoEnabled,
    mainVideo,
    subVideo,
    wasPlayDeniedNoInteraction,
    dispatch,
  ]);

  // manage display media elements
  useEffect(() => {
    if (demoVideo) {
      if (displayStreamOut) {
        demoVideo.muted = true;
        demoVideo.srcObject = displayStreamOut;
        demoVideo.play().catch(error => {
          log.log('There was an error trying to play displaydemo video', error);
        });
      } else {
        demoVideo.srcObject = null;
      }
    }
  }, [demoVideo, displayStreamOut]);
};
